import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import {
    Box,
    Typography,
    Paper,
    Tabs,
    Tab,
    Grid,
    Card,
    CardContent,
    Button,
    Chip,
    Alert,
    CircularProgress,
    Stack,
    TextField,
    InputAdornment,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Badge
} from '@mui/material';
import {
    OndemandVideo,
    PhotoLibrary,
    Build,
    Assignment,
    Search,
    FilterList,
    Security,
    Warning,
    CheckCircle,
    PlayArrow,
    Schedule,
    Engineering,
    Person
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import apiClient from '../api';

// Import safety components (we'll create these)
import SafetyVideoLibrary from '../components/safety/SafetyVideoLibrary';
import LoadSecuringGallery from '../components/safety/LoadSecuringGallery';
import EquipmentList from '../components/safety/EquipmentList';
import RequiredTraining from '../components/safety/RequiredTraining';
import SafetyAnalytics from '../components/safety/SafetyAnalytics';
import SafetyIncidentsList from '../components/safety/SafetyIncidentsList';
import APUTroubleshooting from '../components/safety/APUTroubleshooting';
import APUGuideDetail from '../components/safety/APUGuideDetail';
import SafetyTrainingCompliance from '../components/safety/SafetyTrainingCompliance';
// Import new Samsara safety components
import DriverSafetyScores from '../components/safety/DriverSafetyScores';
import FleetSafetyDashboard from '../components/safety/FleetSafetyDashboard';
import ComplianceReports from '../components/compliance/ComplianceReports';
import samsaraPollingService from '../services/samsaraPollingService';

function SafetyPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState(true);
    const [analytics, setAnalytics] = useState(null);
    const [activeTab, setActiveTab] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterCategory, setFilterCategory] = useState('');
    const [metadata, setMetadata] = useState({ categories: [], equipmentTypes: [], tags: [] });
    const [samsaraConnected, setSamsaraConnected] = useState(false);

    const isAdmin = user?.role === 'admin' || user?.role === 'company_admin';
    const canManageVideos = isAdmin || Boolean(user?.isVideoAdmin);

    // Determine active tab based on current path
    useEffect(() => {
        const path = location.pathname;
        if (path.includes('/safety/videos')) {
            setActiveTab(1);
        } else if (path.includes('/safety/load-securing')) {
            setActiveTab(2);
        } else if (path.includes('/safety/equipment')) {
            setActiveTab(3);
        } else if (path.includes('/safety/apu-troubleshooting')) {
            setActiveTab(4);
        } else if (path.includes('/safety/training')) {
            setActiveTab(5);
        } else if (path.includes('/safety/watch-tracking') || path.includes('/safety/analytics')) {
            setActiveTab(6);
        } else if (path.includes('/safety/driver-scores')) {
            setActiveTab(7);
        } else if (path.includes('/safety/fleet-dashboard')) {
            setActiveTab(8);
        } else if (path.includes('/safety/compliance')) {
            setActiveTab(9);
        } else {
            setActiveTab(0);
        }
    }, [location.pathname]);

    // Fetch safety analytics and metadata
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                
                const [analyticsRes, metadataRes] = await Promise.all([
                    canManageVideos ? apiClient.get('/safety/analytics') : Promise.resolve({ data: null }),
                    apiClient.get('/safety/metadata')
                ]);
                
                if (canManageVideos) {
                    setAnalytics(analyticsRes.data);
                }
                setMetadata(metadataRes.data);
            } catch (error) {
                console.error('Error fetching safety data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [canManageVideos]);

    // Samsara connection and safety-event polling are admin-only.
    useEffect(() => {
        if (!isAdmin) {
            setSamsaraConnected(false);
            return undefined;
        }

        const checkSamsaraConnection = async () => {
            try {
                const isConnected = await samsaraPollingService.checkConnection();
                setSamsaraConnected(isConnected);
            } catch (error) {
                console.warn('Failed to check Samsara connection:', error);
                setSamsaraConnected(false);
            }
        };

        checkSamsaraConnection();

        const unsubscribe = samsaraPollingService.subscribe('safetyEvents', (data, error) => {
            if (error) {
                console.warn('Safety events polling error:', error);
            }
        });

        return () => {
            unsubscribe();
        };
    }, [isAdmin]);

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
        
        // Navigate to appropriate route
        switch (newValue) {
            case 0:
                navigate('/safety');
                break;
            case 1:
                navigate('/safety/videos');
                break;
            case 2:
                navigate('/safety/load-securing');
                break;
            case 3:
                navigate('/safety/equipment');
                break;
            case 4:
                navigate('/safety/apu-troubleshooting');
                break;
            case 5:
                navigate('/safety/training');
                break;
            case 6:
                navigate('/safety/watch-tracking');
                break;
            case 7:
                navigate('/safety/driver-scores');
                break;
            case 8:
                navigate('/safety/fleet-dashboard');
                break;
            case 9:
                navigate('/safety/compliance');
                break;
            default:
                navigate('/safety');
        }
    };

    const SafetyOverview = () => (
        <Box>
            <Typography variant="h5" gutterBottom>
                Safety Resource Center
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
                Access comprehensive safety resources, training materials, and compliance tools 
                to ensure safe operations across all activities.
            </Typography>
            
            {loading ? (
                <Box display="flex" justifyContent="center" p={4}>
                    <CircularProgress />
                </Box>
            ) : (
            <Grid container spacing={3}>
                    {/* Quick Access Cards */}
                    <Grid item xs={12} md={6} lg={3}>
                        <Card sx={{ 
                            height: '100%',
                            transition: 'transform 0.2s',
                            '&:hover': { transform: 'translateY(-4px)' },
                            cursor: 'pointer'
                        }}
                        onClick={() => navigate('/safety/videos')}
                        >
                                        <CardContent>
                                <Stack direction="row" alignItems="center" spacing={2} mb={2}>
                                    <OndemandVideo color="primary" fontSize="large" />
                                    <Typography variant="h6">Training Videos</Typography>
                                </Stack>
                                <Typography variant="body2" color="text.secondary" paragraph>
                                    Access safety instruction videos, Moffett training, and truss trailer operations.
                                            </Typography>
                                <Button 
                                    variant="outlined" 
                                    startIcon={<PlayArrow />}
                                    fullWidth
                                >
                                    Browse Videos
                                </Button>
                                        </CardContent>
                                    </Card>
                </Grid>
                
                    <Grid item xs={12} md={6} lg={3}>
                        <Card sx={{ 
                            height: '100%',
                            transition: 'transform 0.2s',
                            '&:hover': { transform: 'translateY(-4px)' },
                            cursor: 'pointer'
                        }}
                        onClick={() => navigate('/safety/load-securing')}
                        >
                                        <CardContent>
                                <Stack direction="row" alignItems="center" spacing={2} mb={2}>
                                    <PhotoLibrary color="primary" fontSize="large" />
                                    <Typography variant="h6">Load Securing</Typography>
                                </Stack>
                                <Typography variant="body2" color="text.secondary" paragraph>
                                    View examples of properly secured loads linked to customer jobs and best practices.
                                            </Typography>
                                <Button 
                                    variant="outlined" 
                                    startIcon={<PhotoLibrary />}
                                    fullWidth
                                >
                                    View Gallery
                                </Button>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                
                    <Grid item xs={12} md={6} lg={3}>
                        <Card sx={{ 
                            height: '100%',
                            transition: 'transform 0.2s',
                            '&:hover': { transform: 'translateY(-4px)' },
                            cursor: 'pointer'
                        }}
                        onClick={() => navigate('/safety/equipment')}
                        >
                                        <CardContent>
                                <Stack direction="row" alignItems="center" spacing={2} mb={2}>
                                    <Build color="primary" fontSize="large" />
                                    <Typography variant="h6">Equipment</Typography>
                                </Stack>
                                <Typography variant="body2" color="text.secondary" paragraph>
                                    Safety equipment checklists, maintenance guides, and inspection forms.
                                </Typography>
                                <Button 
                                    variant="outlined" 
                                    startIcon={<Build />}
                                    fullWidth
                                >
                                    View Equipment
                                </Button>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                
                    <Grid item xs={12} md={6} lg={3}>
                        <Card sx={{ 
                            height: '100%',
                            transition: 'transform 0.2s',
                            '&:hover': { transform: 'translateY(-4px)' },
                            cursor: 'pointer'
                        }}
                        onClick={() => navigate('/safety/apu-troubleshooting')}
                        >
                                        <CardContent>
                                <Stack direction="row" alignItems="center" spacing={2} mb={2}>
                                    <Engineering color="primary" fontSize="large" />
                                    <Typography variant="h6">APU Troubleshooting</Typography>
                                </Stack>
                                <Typography variant="body2" color="text.secondary" paragraph>
                                    Step-by-step APU troubleshooting guides with video walkthroughs for common issues.
                                </Typography>
                                <Button 
                                    variant="outlined" 
                                    startIcon={<Engineering />}
                                    fullWidth
                                >
                                    View Guides
                                </Button>
                                        </CardContent>
                                    </Card>
                </Grid>
                
                    <Grid item xs={12} md={6} lg={3}>
                        <Card sx={{ 
                            height: '100%',
                            transition: 'transform 0.2s',
                            '&:hover': { transform: 'translateY(-4px)' },
                            cursor: 'pointer'
                        }}
                        onClick={() => navigate('/safety/training')}
                        >
                                        <CardContent>
                                <Stack direction="row" alignItems="center" spacing={2} mb={2}>
                                    <Assignment color="primary" fontSize="large" />
                                    <Typography variant="h6">Required Training</Typography>
                                </Stack>
                                <Typography variant="body2" color="text.secondary" paragraph>
                                    Track completion of mandatory safety videos and training requirements.
                                            </Typography>
                                <Button 
                                    variant="outlined" 
                                    startIcon={<Schedule />}
                                    fullWidth
                                >
                                    View Requirements
                                </Button>
                                        </CardContent>
                                    </Card>
                </Grid>
                
                    {/* Quick Stats */}
                    {analytics && (
                <Grid item xs={12}>
                    <Paper sx={{ p: 3, mt: 2 }}>
                        <Typography variant="h6" gutterBottom>
                                    Safety Dashboard
                        </Typography>
                        <Grid container spacing={2}>
                                    <Grid item xs={6} md={3}>
                                        <Card variant="outlined">
                                            <CardContent sx={{ textAlign: 'center' }}>
                                                <Typography variant="h4" color="success.main">
                                                    {analytics.completionStats?.find(s => s._id === 'completed')?.count || 0}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Completed Training
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <Card variant="outlined">
                                            <CardContent sx={{ textAlign: 'center' }}>
                                                <Typography variant="h4" color="warning.main">
                                                    {analytics.completionStats?.find(s => s._id === 'in_progress')?.count || 0}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                                    In Progress
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                                    <Grid item xs={6} md={3}>
                                        <Card variant="outlined">
                                            <CardContent sx={{ textAlign: 'center' }}>
                                                <Typography variant="h4" color="error.main">
                                                    {analytics.overdueCount || 0}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                                    Overdue Items
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                                    <Grid item xs={6} md={3}>
                                        <Card variant="outlined">
                                            <CardContent sx={{ textAlign: 'center' }}>
                                                <Typography variant="h4" color="info.main">
                                                    {analytics.dueForReview || 0}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                                    Due for Review
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>
                    )}
                    {isAdmin && (
                    <Grid item xs={12}>
                        <SafetyIncidentsList />
                    </Grid>
                    )}
            </Grid>
            )}
        </Box>
    );

    return (
        <Box sx={{ pt: 0 }}>
            {/* Compact Header */}
            <Paper sx={{ p: 1, mb: 1.5, borderRadius: 2, borderTop: '3px solid', borderTopColor: 'success.main' }}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Security color="success" />
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Safety Center
                    </Typography>
                </Stack>
            </Paper>

            {/* Search and Filter Bar */}
            <Paper sx={{ p: 1, mb: 1.5, borderRadius: 2, borderTop: '3px solid', borderTopColor: 'success.main' }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
                    <TextField
                        placeholder="Search safety content..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search />
                                </InputAdornment>
                            ),
                        }}
                        sx={{ flexGrow: 1, minWidth: { xs: '100%', md: 300 } }}
                    />
                    
                    <FormControl sx={{ minWidth: 200 }}>
                        <InputLabel>Category</InputLabel>
                        <Select
                            value={filterCategory}
                            label="Category"
                            onChange={(e) => setFilterCategory(e.target.value)}
                            startAdornment={<FilterList sx={{ mr: 1 }} />}
                        >
                            <MenuItem value="">All Categories</MenuItem>
                            {metadata.categories.map((category) => (
                                <MenuItem key={category} value={category}>
                                    {category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Stack>
            </Paper>

            {/* Navigation Tabs */}
            <Paper sx={{ mb: 1.5, borderRadius: 2, bgcolor: 'rgba(76, 175, 80, 0.06)' }}>
                <Tabs 
                    value={activeTab} 
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{ borderBottom: 1, borderColor: 'divider' }}
                >
                    <Tab 
                        label="Overview" 
                        icon={<Security />}
                    />
                    <Tab 
                        label="Training Videos" 
                        icon={<OndemandVideo />}
                    />
                    <Tab 
                        label="Load Securing" 
                        icon={<PhotoLibrary />}
                    />
                    <Tab 
                        label="Equipment" 
                        icon={<Build />}
                    />
                    <Tab 
                        label="APU Troubleshooting" 
                        icon={<Engineering />}
                    />
                    <Tab 
                        label="Required Training" 
                        icon={
                            <Badge 
                                badgeContent={analytics?.overdueCount || 0} 
                                color="error"
                                invisible={!analytics?.overdueCount}
                            >
                                <Assignment />
                            </Badge>
                        }
                    />
                    {canManageVideos && (
                        <Tab 
                            label="Watch Tracking" 
                            icon={<Assignment />}
                        />
                    )}
                    {samsaraConnected && (
                        <>
                            <Tab 
                                label="Driver Scores" 
                                icon={<Person />}
                            />
                            <Tab 
                                label="Fleet Dashboard" 
                                icon={<Security />}
                            />
                            <Tab 
                                label="Compliance" 
                                icon={<Assignment />}
                            />
                        </>
                    )}
                </Tabs>
            </Paper>

            {/* Content Area */}
            <Routes>
                <Route path="/" element={<SafetyOverview />} />
                <Route path="/videos" element={
                    <SafetyVideoLibrary 
                        searchTerm={searchTerm}
                        filterCategory={filterCategory}
                        metadata={metadata}
                    />
                } />
                <Route path="/load-securing" element={
                    <LoadSecuringGallery 
                        searchTerm={searchTerm}
                        filterCategory={filterCategory}
                    />
                } />
                <Route path="/equipment" element={
                    <EquipmentList 
                        searchTerm={searchTerm}
                        filterCategory={filterCategory}
                        metadata={metadata}
                    />
                } />
                <Route path="/apu-troubleshooting" element={<APUTroubleshooting />} />
                <Route path="/apu-troubleshooting/:id" element={<APUGuideDetail />} />
                <Route path="/training" element={
                    <RequiredTraining 
                        analytics={analytics}
                    />
                } />
                {canManageVideos && (
                    <Route path="/watch-tracking" element={
                        <Box>
                            <SafetyTrainingCompliance />
                            {analytics && (
                                <Box sx={{ mt: 4 }}>
                                    <SafetyAnalytics analytics={analytics} />
                                </Box>
                            )}
                        </Box>
                    } />
                )}
                {canManageVideos && (
                    <Route path="/analytics" element={
                        <Box>
                            <SafetyTrainingCompliance />
                            {analytics && (
                                <Box sx={{ mt: 4 }}>
                                    <SafetyAnalytics analytics={analytics} />
                                </Box>
                            )}
                        </Box>
                    } />
                )}
                {samsaraConnected && (
                    <>
                        <Route path="/driver-scores" element={<DriverSafetyScores />} />
                        <Route path="/fleet-dashboard" element={<FleetSafetyDashboard />} />
                        <Route path="/compliance" element={<ComplianceReports />} />
                    </>
                )}
            </Routes>
        </Box>
    );
}

export default SafetyPage; 